// Confetti color schemes for different holidays and seasons
// The confetti colors will automatically change based on the current date
// to match various holidays throughout the year
const HOLIDAY_COLORS = {
  // celebrate colors (default) (bright and cheerful)
  celebrate: ['#ff6b6b', '#4ecdc4', '#ffe66d', '#a8e6cf', '#ff8b94', '#a8e6cf'],

  // Christmas colors - traditional red, green, gold, snow white
  christmas: [
    '#ff0000',
    '#00ff00',
    '#ffd700',
    '#f0f8ff',
    '#dc143c',
    '#228b22',
    '#ffd700',
    '#ffffff',
  ],

  // Halloween colors
  halloween: ['#ff8c00', '#800080', '#000000', '#ff4500', '#32cd32', '#ff1493'],

  // Valentine's Day colors
  valentines: [
    '#ff69b4',
    '#ff1493',
    '#ffb6c1',
    '#dc143c',
    '#ffc0cb',
    '#ff69b4',
  ],

  // St. Patrick's Day colors - green with gold
  stPatricks: [
    '#228b22',
    '#32cd32',
    '#90ee90',
    '#98fb98',
    '#ffd700',
    '#ffed4e',
  ],

  // Fourth of July colors - red, white, blue only
  fourthOfJuly: [
    '#ff0000',
    '#ffffff',
    '#0000ff',
    '#dc143c',
    '#f0f8ff',
    '#4169e1',
  ],

  // Thanksgiving colors - brown, orange, gold, cranberry red
  thanksgiving: [
    '#8b4513',
    '#daa520',
    '#ff6347',
    '#228b22',
    '#ffd700',
    '#dc143c',
  ],

  // Easter colors
  easter: ['#ffb6c1', '#87ceeb', '#98fb98', '#dda0dd', '#f0e68c', '#ff69b4'],

  // New Year colors - classic white, black, gold, silver
  newYear: ['#ffffff', '#000000', '#ffd700', '#c0c0c0', '#f0f8ff', '#d3d3d3'],

  // Birthday colors - neon bright
  birthday: [
    '#ff1493',
    '#00ff00',
    '#00bfff',
    '#ffff00',
    '#ff4500',
    '#ff00ff',
    '#00ffff',
    '#ff69b4',
  ],

  // Pride Month colors - proper rainbow
  pride: ['#E50000', '#FF8D00', '#FFEE00', '#028121', '#004CFF', '#770088'],

  // Chinese New Year colors - red and gold only (2/3 red, 1/3 gold)
  chineseNewYear: [
    '#ff0000',
    '#dc143c',
    '#b22222',
    '#ffd700',
    '#ffed4e',
    '#ff0000',
    '#dc143c',
    '#ffd700',
  ],

  // Hanukkah colors (blue and white)
  hanukkah: [
    '#0000ff',
    '#4169e1',
    '#1e90ff',
    '#00bfff',
    '#87ceeb',
    '#ffffff',
    '#f0f8ff',
    '#e6f3ff',
    '#b0e0e6',
    '#add8e6',
  ],

  // Kwanzaa colors (red, green, black)
  kwanzaa: [
    '#ff0000',
    '#00ff00',
    '#000000',
    '#228b22',
    '#32cd32',
    '#006400',
    '#8b0000',
    '#dc143c',
    '#ff4500',
    '#ff6347',
  ],

  // Earth Day colors - nature tones with browns
  earthDay: [
    '#228b22',
    '#32cd32',
    '#90ee90',
    '#8b4513',
    '#a0522d',
    '#556b2f',
    '#8fbc8f',
    '#2e8b57',
  ],

  // Mardi Gras colors - purple, green, gold only
  mardiGras: ['#800080', '#32cd32', '#ffd700', '#9370db', '#90ee90', '#ffed4e'],

  // Cinco de Mayo colors - Mexican flag colors only
  cincoDeMayo: [
    '#006847',
    '#ffffff',
    '#ce1126',
    '#ffd700',
    '#228b22',
    '#dc143c',
  ],
};

// Holiday date ranges (month, day) - sorted chronologically
const HOLIDAY_DATES = {
  newYear: { start: { month: 12, day: 28 }, end: { month: 1, day: 3 } },
  chineseNewYear: { start: { month: 1, day: 20 }, end: { month: 2, day: 10 } },
  valentines: { start: { month: 2, day: 14 }, end: { month: 2, day: 14 } },
  mardiGras: { start: { month: 2, day: 15 }, end: { month: 3, day: 5 } },
  stPatricks: { start: { month: 3, day: 17 }, end: { month: 3, day: 17 } },
  easter: { start: { month: 3, day: 20 }, end: { month: 4, day: 15 } },
  earthDay: { start: { month: 4, day: 22 }, end: { month: 4, day: 22 } },
  cincoDeMayo: { start: { month: 5, day: 5 }, end: { month: 5, day: 5 } },
  birthday: { start: { month: 6, day: 1 }, end: { month: 6, day: 1 } },
  pride: { start: { month: 6, day: 2 }, end: { month: 6, day: 30 } },
  fourthOfJuly: { start: { month: 7, day: 4 }, end: { month: 7, day: 4 } },
  halloween: { start: { month: 10, day: 31 }, end: { month: 10, day: 31 } },
  thanksgiving: { start: { month: 11, day: 20 }, end: { month: 11, day: 24 } },
  hanukkah: { start: { month: 11, day: 25 }, end: { month: 12, day: 15 } },
  christmas: { start: { month: 12, day: 1 }, end: { month: 12, day: 25 } },
  kwanzaa: { start: { month: 12, day: 26 }, end: { month: 1, day: 1 } },
};

// Helper function to check if a date falls within a holiday range
const isDateInRange = (date, start, end) => {
  const currentMonth = date.getMonth() + 1; // getMonth() returns 0-11
  const currentDay = date.getDate();

  // Handle year wrap-around (like New Year)
  if (start.month > end.month) {
    return (
      (currentMonth >= start.month && currentDay >= start.day) ||
      (currentMonth <= end.month && currentDay <= end.day)
    );
  }

  // Normal date range
  if (currentMonth === start.month) {
    return currentDay >= start.day;
  } else if (currentMonth === end.month) {
    return currentDay <= end.day;
  } else {
    return currentMonth > start.month && currentMonth < end.month;
  }
};

// Get the appropriate color scheme for the current date
export const getConfettiColors = () => {
  const today = new Date();

  // Check each holiday
  for (const [holiday, dateRange] of Object.entries(HOLIDAY_DATES)) {
    if (isDateInRange(today, dateRange.start, dateRange.end)) {
      return HOLIDAY_COLORS[holiday];
    }
  }

  // Return celebrate colors if no holiday matches
  return HOLIDAY_COLORS.celebrate;
};

// Export individual color schemes for testing or manual use
export const confettiColorSchemes = HOLIDAY_COLORS;
